import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/StatusBadge";
import { RecordDetail } from "@/components/RecordDetail";
import { PageHeader } from "@/components/PageHeader";
import { formatDate } from "@/lib/format";
import { Search } from "lucide-react";

export default function Clients() {
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const { data: clients = [], isLoading } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => [c.name, c.phone, c.city, c.email].some((v) => String(v || "").toLowerCase().includes(q)));
  }, [clients, search]);

  return (
    <div data-testid="clients-page">
      <PageHeader title="Clients" subtitle={`${filtered.length} clients`} />
      <div className="relative max-w-xs mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input data-testid="clients-search-input" placeholder="Search name, phone, city…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white dark:bg-slate-950" />
      </div>

      <Card className="border-slate-200 dark:border-slate-800 overflow-hidden p-0">
        <Table data-testid="clients-table">
          <TableHeader>
            <TableRow className="bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-50">
              <TableHead className="uppercase text-xs tracking-wider">Name</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Phone</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">City</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Owner</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Status</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && Array.from({ length: 5 }).map((_, i) => <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-8 w-full" /></TableCell></TableRow>)}
            {!isLoading && filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">No clients found.</TableCell></TableRow>}
            {!isLoading && filtered.map((c) => (
              <TableRow key={c.id} className="cursor-pointer hover:bg-slate-50/80 dark:hover:bg-slate-800/40" data-testid={`client-row-${c.id}`} onClick={() => setDetail(c)}>
                <TableCell className="font-medium text-slate-900 dark:text-slate-100">{c.name}</TableCell>
                <TableCell className="font-num text-slate-600 dark:text-slate-300">{c.phone || "—"}</TableCell>
                <TableCell className="text-slate-600 dark:text-slate-300">{c.city || "—"}</TableCell>
                <TableCell className="text-slate-600 dark:text-slate-300">{c.owner_name || "—"}</TableCell>
                <TableCell><StatusBadge status={c.status} /></TableCell>
                <TableCell className="text-slate-500 text-sm">{formatDate(c.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <RecordDetail open={!!detail} onOpenChange={(o) => !o && setDetail(null)} title={detail?.name || "Client"} record={detail} />
    </div>
  );
}
